import React, { createContext, useContext, useState, useEffect } from 'react';
import { House, City, PropertyManager, BaseUser } from '@/types/admin';
import { se } from 'date-fns/locale';

interface StatsFiltersContextType {
  selectedHouse: House | undefined;
  setSelectedHouse: (house: House | undefined) => void;
  housesQuery: string;
  setHousesQuery: (query: string) => void;
  startDate: string | undefined;
  endDate: string | undefined;
  cityId: string | null;
  propertyManagerId: string | null;
  setStartDate: (date: string | Date | undefined) => void;
  setEndDate: (date: string | Date | undefined) => void;
  setCityId: (id: string | null) => void;
  setOwnerId: (id: string | null) => void;
  setPropertyManagerId: (id: string | null) => void;
  colocationId: string | null;
  setColocationId: (id: string | null) => void;
  selectedCity: City | undefined;
  setSelectedCity: (city: City | undefined) => void;
  citiesQuery: string;
  setCitiesQuery: (query: string) => void;
  selectedPropertyManager: PropertyManager | undefined;
  setSelectedPropertyManager: (manager: PropertyManager | undefined) => void;
  selectedOwner: BaseUser | undefined;
  setSelectedOwner: (owner: BaseUser | undefined) => void;
  selectedVacancyHouse: House | undefined;
  setSelectedVacancyHouse: (house: House | undefined) => void;
  vacancyHousesQuery: string;
  setVacancyHousesQuery: (query: string) => void;
  selectedVacancyStatus: 'all' | 'vacant' | 'maintenance' | undefined;
  setSelectedVacancyStatus: (status: 'all' | 'vacant' | 'maintenance' | undefined) => void;
}

const StatsFiltersContext = createContext<StatsFiltersContextType | undefined>(undefined);

export const StatsFiltersProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [selectedHouse, setSelectedHouse] = useState<House | undefined>();
  const [housesQuery, setHousesQuery] = useState('');
  const [startDate, setStartDate] = useState<string | Date | undefined>(
    new Date(
      new Date(new Date().getFullYear(), 0, 1).getTime() - new Date().getTimezoneOffset() * 60000
    )
  );
  const [endDate, setEndDate] = useState<string | Date | undefined>(
    new Date(
      new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getTime() -
        new Date().getTimezoneOffset() * 60000
    )
  );
  const [cityId, setCityId] = useState<string | null>(null);
  const [propertyManagerId, setPropertyManagerId] = useState<string | null>(null);
  const [ownerId, setOwnerId] = useState<string | null>(null);
  const [colocationId, setColocationId] = useState<string | null>(null);
  const [selectedCity, setSelectedCity] = useState<City | undefined>();
  const [selectedOwner, setSelectedOwner] = useState<BaseUser | undefined>();
  const [citiesQuery, setCitiesQuery] = useState('');
  const [selectedPropertyManager, setSelectedPropertyManager] = useState<
    PropertyManager | undefined
  >();
  const [selectedVacancyHouse, setSelectedVacancyHouse] = useState<House | undefined>();
  const [vacancyHousesQuery, setVacancyHousesQuery] = useState('');
  const [selectedVacancyStatus, setSelectedVacancyStatus] = useState<
    'all' | 'vacant' | 'maintenance' | undefined
  >('all');

  // Sync colocationId with selectedHouse
  useEffect(() => {
    if (selectedHouse) {
      setColocationId(selectedHouse.id.toString());
    } else {
      setColocationId(null);
    }
  }, [selectedHouse]);

  // Sync selectedHouse with colocationId
  useEffect(() => {
    if (!colocationId) {
      setSelectedHouse(undefined);
    }
  }, [colocationId]);

  // Sync cityId with selectedCity
  useEffect(() => {
    if (selectedCity) {
      setCityId(selectedCity.id.toString());
    } else {
      setCityId(null);
    }
  }, [selectedCity]);

  // Sync selectedCity with cityId
  useEffect(() => {
    if (!cityId) {
      setSelectedCity(undefined);
    }
  }, [cityId]);

  useEffect(() => {
    if (selectedOwner) {
      setOwnerId(selectedOwner.id.toString());
    } else {
      setOwnerId(null);
    }
  }, [ownerId]);

  // Sync propertyManagerId with selectedPropertyManager
  useEffect(() => {
    if (selectedPropertyManager) {
      setPropertyManagerId(selectedPropertyManager.id.toString());
    } else {
      setPropertyManagerId(null);
    }
  }, [selectedPropertyManager]);

  // Sync selectedPropertyManager with propertyManagerId
  useEffect(() => {
    if (!propertyManagerId) {
      setSelectedPropertyManager(undefined);
    }
  }, [propertyManagerId]);

  return (
    <StatsFiltersContext.Provider
      value={{
        selectedHouse,
        setSelectedHouse,
        housesQuery,
        setHousesQuery,
        startDate,
        endDate,
        cityId,
        propertyManagerId,
        setStartDate,
        setEndDate,
        setCityId,
        setPropertyManagerId,
        colocationId,
        setColocationId,
        selectedCity,
        setSelectedCity,
        selectedOwner,
        setSelectedOwner,
        citiesQuery,
        setCitiesQuery,
        selectedPropertyManager,
        setSelectedPropertyManager,
        selectedVacancyHouse,
        setSelectedVacancyHouse,
        vacancyHousesQuery,
        setVacancyHousesQuery,
        selectedVacancyStatus,
        setSelectedVacancyStatus,
      }}
    >
      {children}
    </StatsFiltersContext.Provider>
  );
};

export const useStatsFiltersContext = () => {
  const context = useContext(StatsFiltersContext);
  if (context === undefined) {
    throw new Error('useStatsFiltersContext must be used within a StatsFiltersProvider');
  }
  return context;
};
